import { useTranslation } from 'react-i18next';
import '@/utils/notifications';
import type { ProgramListItemDataFragment } from '../ProgramListing/ProgramListing.api';
import type { ProgramFormData } from '../shared/ProgramFormConfig';
import ProgramFormContainer from '../shared/ProgramFormContainer';
import type { UpdateProgramMutation, UpdateProgramMutationVariables } from './UpdateProgram.api';
import { UpdateProgramDocument } from './UpdateProgram.api';
import client from '@/apolloClient';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

export interface UpdateProgramProps {
    open: boolean;
    program?: ProgramListItemDataFragment;
    companyId: string;
    onClose: () => void;
    onCreated: () => void;
}

const EditProgram = ({ open, onClose, onCreated, companyId, program }: UpdateProgramProps) => {
    const { t } = useTranslation('programManagementPage');

    const handleSubmit = async (formData: ProgramFormData) => {
        const { name, description } = formData;
        try {
            const variables: UpdateProgramMutationVariables = {
                id: program?.id || '',
                companyId: companyId || '',
                input: { name, description },
            };

            const { data } = await client.mutate<UpdateProgramMutation>({
                mutation: UpdateProgramDocument,
                variables,
            });

            if (data?.updateProgram?.__typename === 'UpdateProgramSuccessfulResponse') {
                if (onCreated) {
                    onCreated();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('notifications.updateTitle'),
                    description: t('notifications.updateDescription', { name: formData.name }),
                });
                return;
            }

            if (data?.updateProgram?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.updateProgram.fields,
                };
                showErrorNotification({
                    message: t('notifications.updateErrorTitle'),
                    description: error.fields?.[0]?.message || t('notifications.updateErrorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error updating program');
        } catch (error) {
            console.error('Error updating program:', error);
            // Handle error appropriately
        }
    };
    return (
        <ProgramFormContainer
            mode="edit"
            program={program}
            companyId={companyId}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            title={t('modalEditProgram.title')}
        />
    );
};

export default EditProgram;
